import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { RoomStatusEnum } from '@zaoblako/shared/modules/catalog/enums/room-status.enum';
import { RoomStatusEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/room-status-enum.i18n';
import { RoomTypeEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/room-type-enum.i18n';
import { RoomTypeEnum } from '@zaoblako/shared/modules/catalog/enums/room-type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { entityLikeSchema, entityLikeSchemaUuid } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { StorageBackendValidatorsEnum } from '@zaoblako/shared/modules/storage/enums/storage-backend-validators.enum';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { NetPingDeviceFindInterface } from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-find.dto';
import type { ConverterFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { OnlyIdRequiredInterface } from '@zaoblako/shared/common/interfaces/only-id-required.interface';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';

import * as yup from 'yup';

/** Room create/update request body */
export interface RoomFormInterface {
  /** Display name. */
  name: string;
  /** Room area in square meters */
  square?: number;
  /** Room floor number */
  floor?: number;
  /** Number of parking places */
  parkingPlacesCount?: number;
  /** Room type */
  type?: RoomTypeEnum;
  /** Room status */
  status: RoomStatusEnum;
  /** Finish condition description */
  finishCondition?: string;
  /** Room plan file */
  roomPlan?: OnlyIdRequiredInterface<FileFindInterface>;
  /** Floor plan file */
  floorPlan?: OnlyIdRequiredInterface<FileFindInterface>;
  /** Related filial */
  filial?: FilialFindInterface;
  /** Related client */
  client?: ClientFindInterface;
  /** "NetPing" devices controlling access to this room */
  netPingDevices?: NetPingDeviceFindInterface[];
  /** Room entrance converters */
  converters?: ConverterFindInterface[];
  /** Related users who have access to the premises */
  allowUsers?: UserFindInterface[];
}

/** Room create/update request body - DTO schema */
export const RoomFormDto = SerdeDtoClass.create<RoomFormInterface>(CatalogDtosKeysEnum.ROOM_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  square: {
    optional: true,
    label: 'Площадь',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE),
  },
  floor: {
    optional: true,
    label: 'Этаж',
    serializer: yup
      .number()
      .integer(DtoMessageEnum.NOT_INTEGER),
  },
  parkingPlacesCount: {
    optional: true,
    label: 'Кол-во парковочных мест',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE)
      .integer(DtoMessageEnum.NOT_INTEGER),
  },
  type: {
    optional: true,
    label: 'Тип',
    serializer: commonEnumValidator(RoomTypeEnum, RoomTypeEnumI18n),
  },
  status: {
    label: 'Статус',
    serializer: commonEnumValidator(RoomStatusEnum, RoomStatusEnumI18n),
  },
  finishCondition: {
    optional: true,
    label: 'Состояние отделки',
    serializer: yup.string(),
  },
  roomPlan: {
    optional: true,
    label: 'План помещения',
    serializer: entityLikeSchemaUuid,
    checkers: [StorageBackendValidatorsEnum.FILE_ASSERT_EXISTS_BY_DTO],
  },
  floorPlan: {
    optional: true,
    label: 'Расположение на плане этажа',
    serializer: entityLikeSchemaUuid,
    checkers: [StorageBackendValidatorsEnum.FILE_ASSERT_EXISTS_BY_DTO],
  },
  filial: {
    optional: true,
    label: 'Филиал',
    serializer: entityLikeSchema,
  },
  client: {
    optional: true,
    label: 'Клиент',
    serializer: entityLikeSchema,
  },
  netPingDevices: {
    optional: true,
    label: 'Устройства "netPing"',
    serializer: yup.array(entityLikeSchema),
  },
  converters: {
    optional: true,
    label: 'Конвертеры',
    serializer: yup.array(entityLikeSchema),
  },
  allowUsers: {
    optional: true,
    label: 'Пользователи, имеющие доступ к помещению',
    serializer: yup.array(entityLikeSchema),
  },
});
